/**
 * MOON FOUNDRY TAB NAMES ARE NEVER ABBREVIATED.
 *
 * The Foundry command bar carries the Lobby button, both panel tabs, a price
 * note, the Stardust pill, the language select and — on a compact viewport —
 * the Moon Fuse inventory search, all competing for one row. What used to give
 * was the NAVIGATION: `text-overflow: ellipsis` on `.foundry-tab` left a player
 * choosing between "Moon F…" and "Moon Dre…", and it bit sooner in French,
 * German and Portuguese than in the English the layout was tuned against.
 *
 * Shortening a name is not one of the options here. The rule the owner set is
 * that when a label will not fit, the TYPE gets smaller and the WORD stays
 * whole. This module is that rule: it measures the real laid-out ink against
 * the real box and publishes one shared scale, `--foundry-tab-fit`, that every
 * `.foundry-tab` font-size multiplies by. One scale for the whole row, so the
 * bar keeps a single type size rather than reading like a ransom note.
 *
 * WHY MEASURED, AND NOT A `clamp()`. A stylesheet cannot see its own text. The
 * eight shipped languages disagree by more than a third on the width of the
 * same tab ("Moon Fuse" against "Fusion lunaire"), so a clamp tuned for the
 * widest one would shrink every other language for no reason at all. Measuring
 * spends the width only where it is genuinely short, which is the whole point
 * of "smaller type as a LAST resort".
 *
 * WHY THIS CANNOT CAUSE A LOAD-TIME REFLOW. The surface stays
 * `visibility: hidden` until Cinzel and Spectral have activated and the
 * opening tab's first view has been read (`.foundry-surface-preparing`), and
 * visibility-hidden boxes still have layout.
 * The first fit therefore runs against the final faces, synchronously, in the
 * same task that reveals the surface — the player's first frame is already the
 * fitted one. Every later fit is likewise synchronous with the thing that
 * caused it (a language swap re-measures inside `moonfall:i18n-translated`);
 * only a viewport resize, which is a continuous gesture and not a load, is
 * coalesced into an animation frame.
 *
 * WHAT IT DELIBERATELY DOES NOT WATCH. The price note settles from a long
 * placeholder ("Chargement des coûts de fusion...") to a short number after the
 * surface is already painted. Re-fitting then would move the tabs after first
 * paint, which the repo forbids, so the fit is left alone — and it is safe to
 * leave alone in that direction, because the placeholder is the WIDER of the
 * two and `foundry.css` caps the note at `6.5em` on every landscape phone
 * anyway. The worst case is a tab a hair smaller than it strictly had to be,
 * which is a better trade than a row that rearranges itself under the player.
 */

import { uiPx } from './ui-scale.js';

/**
 * The smallest type the bar may use. Below this the name is present but not
 * readable, which is the same defeat as truncating it by another route — so
 * the floor is a real floor, and a row that cannot fit even here keeps its
 * words and lets the *search field* lose the argument instead (the tablist is
 * pinned to its content width in `foundry.css`).
 *
 * 11px is not a taste call: it is the repo's published phone-legibility floor.
 * `tests/e2e-playwright/real/mobile-defects.real.spec.js` reports any button
 * rendering below it as a defect, and `foundryMobileTypography.test.js` names
 * it "the 11px glanceable floor". Trading a truncated label for an unreadable
 * one would be the same defeat wearing a different coat.
 *
 * The stylesheet enforces it too — every tab font-size is written
 * `max(11px, <authored> * var(--foundry-tab-fit, 1))` — so a fit that
 * miscalculates cannot render smaller than this even in principle.
 */
const MIN_FONT_PX = 11;

/** Half a pixel of slack: an exact fit must not oscillate on sub-pixel rounding. */
const SLACK = 0.5;

/**
 * A hairline under the solved ratio. Glyph advance widths are very nearly, but
 * not exactly, linear in font-size once hinting is applied.
 */
const SOLVE_MARGIN = 0.995;

/**
 * Two passes settle it — the solve is exact for the linear part and the second
 * absorbs rounding — and the third is a backstop, not an expectation.
 */
const MAX_PASSES = 3;

/** Below this the change is invisible and another layout pass is not worth it. */
const SETTLED = 0.005;

function clamp(value, low, high) {
  return Math.min(high, Math.max(low, value));
}

function px(value) {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
}

/**
 * The largest scale at or below 1 at which every tab label fits its own box and
 * the row fits the bar. Pure: it takes measurements and returns a number, so
 * the arithmetic can be tested without a layout engine.
 *
 * `scalable` is the part of a tab's width that shrinks with the type (its
 * labels and glyphs); `room` is what the tab's content box leaves for that part
 * once padding, border and gaps — which do NOT shrink — are taken out.
 *
 * @param {object} state
 * @param {number} [state.scale] the scale the measurements were taken at
 * @param {Array<{scalable:number, room:number}>} [state.tabs]
 * @param {number} [state.barOverflow] the bar's scrollWidth minus its clientWidth
 * @param {number} [state.minScale] the legibility floor, as a ratio of the
 *   authored size; anything non-finite is read as "no floor"
 * @returns {number} the scale to apply next, always finite and in [minScale, 1]
 */
function solveTabFitScale({ scale = 1, tabs, barOverflow = 0, minScale } = {}) {
  /* A default parameter only covers `undefined`, so `tabs: null` would reach
     the loop and throw — out of a `moonfall:i18n-translated` listener, in the
     middle of a language change. And a NaN floor propagates straight through
     `Math.max`/`Math.min` into the returned scale, which is the one value this
     module must never produce. Both are normalised here rather than guarded at
     the call sites, so the function is safe for every caller it will ever have. */
  const measured = Array.isArray(tabs) ? tabs : [];
  const floor = Number.isFinite(minScale) ? clamp(minScale, 0, 1) : 0;
  const current = clamp(Number(scale) || 0, floor, 1);
  let ratio = 1;

  for (const tab of measured) {
    const scalable = Number(tab?.scalable) || 0;
    const room = Number(tab?.room) || 0;
    // Nothing that shrinks with the type, or a box that already holds it.
    if (scalable <= 0 || scalable <= room + SLACK) continue;
    ratio = Math.min(ratio, room / scalable);
  }

  const overflow = Number(barOverflow) || 0;
  if (overflow > SLACK) {
    // Only the tab type yields; the padding, the gaps and every other control
    // in the row are fixed as far as this lever is concerned. So the deficit
    // has to come out of the scalable ink alone.
    const scalable = measured.reduce(
      (sum, tab) => sum + Math.max(0, Number(tab?.scalable) || 0), 0,
    );
    /* No ink at all means this lever cannot move the row one pixel — a tab that
       lost its label span to a markup change looks exactly like this. Shrinking
       to the floor there would make the type small and fix nothing, so the
       overflow is reported by the row's own clip instead of being answered with
       a cosmetic change that hides the real breakage. */
    if (scalable > 0) {
      ratio = Math.min(ratio, scalable > overflow ? (scalable - overflow) / scalable : 0);
    }
  }

  if (ratio >= 1) return current;
  return clamp(current * ratio * SOLVE_MARGIN, floor, 1);
}

/**
 * One tab's fixed and scalable widths. Children that are out of flow — the
 * screen-reader-only Lobby word on a narrow phone is one — occupy no width and
 * take no part in the fit.
 */
function measureTab(tab) {
  const style = getComputedStyle(tab);
  const width = tab.getBoundingClientRect().width;
  const gap = px(style.columnGap); // `normal` on a non-flex box parses to 0
  let scalable = 0;
  let inFlow = 0;

  for (const child of tab.children) {
    const childStyle = getComputedStyle(child);
    if (childStyle.display === 'none') continue;
    if (childStyle.position === 'absolute' || childStyle.position === 'fixed') continue;
    // A `white-space: nowrap` inline-block never shrinks below its ink, so this
    // rect is the width the label actually WANTS even while it overflows.
    scalable += child.getBoundingClientRect().width;
    inFlow += 1;
  }

  const fixed = px(style.paddingLeft) + px(style.paddingRight)
    + px(style.borderLeftWidth) + px(style.borderRightWidth)
    + (inFlow > 1 ? gap * (inFlow - 1) : 0);

  return { scalable, room: Math.max(0, width - fixed) };
}

function applyScale(bar, scale) {
  /* NEVER PUBLISH A VALUE CSS CANNOT PARSE. `font-size: max(11px, 13px *
     var(--foundry-tab-fit, 1))` is invalid at computed-value time if the
     substitution is not a number, and an invalid font-size does not fall back
     to the declaration — it falls back to INHERITED, which would silently
     resize every tab to whatever the bar inherits. The fallback in `var()`
     cannot save it either, because a set-but-unparsable property is still
     "set". One NaN would therefore be a whole-bar typography bug with no error
     anywhere, so it never leaves this function. */
  if (!Number.isFinite(scale)) return;
  // Three decimals is finer than any sub-pixel a font size resolves to, and it
  // keeps the published value stable enough to compare between passes.
  const value = String(Math.round(scale * 1000) / 1000);
  /* Writing the same value still invalidates style, and the FIRST thing every
     fit does is reset to 1 — which, at every viewport measured so far, is
     already the value. On a window drag that is a forced style recalc per
     frame for nothing. */
  if (bar.style.getPropertyValue('--foundry-tab-fit') === value) return;
  bar.style.setProperty('--foundry-tab-fit', value);
}

/** The passes for the row as it is laid out now, from the authored size. */
function solveLaidOutRow(bar, tabs, minScale) {
  applyScale(bar, 1);
  let scale = 1;
  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    const next = solveTabFitScale({
      scale,
      tabs: tabs.map(measureTab),
      barOverflow: bar.scrollWidth - bar.clientWidth,
      minScale,
    });
    if (Math.abs(next - scale) < SETTLED) break;
    scale = next;
    applyScale(bar, scale);
  }
  return scale;
}

/**
 * ONE SIZE FOR BOTH TABS. The tools beside the tabs belong to a tab — on a
 * compact viewport Moon Fuse seats its inventory search there, Moon Dream only
 * its balance — so a row fitted to the tools on screen needed a smaller type on
 * one tab than on the other, and every switch resized both names under the
 * finger that had just pressed one: the tab bar morphing on each tab switch.
 * The scale is therefore solved for each tab's tools in turn and the smallest
 * one wins, so the names are the same size whichever tab shows. Each set is
 * shown alone for its measurement and the row is restored before this returns:
 * all of it inside one task, so none of it is ever painted.
 *
 * Except while the player is IN those tools (typing a search, a focused
 * balance pill): hiding a focused control can blur it, and a keyboard closing
 * mid-word is worse than a type size solved for the row as it stands. The
 * next fit with the focus elsewhere — the tab press itself — solves them all.
 */
function solveEveryTabsRow(bar, tabs, minScale) {
  const toolSets = Array.from(bar.querySelectorAll('[data-foundry-tools-for]'));
  const focused = typeof document !== 'undefined' ? document.activeElement : null;
  if (toolSets.length < 2 || toolSets.some((tools) => tools.contains(focused))) {
    return solveLaidOutRow(bar, tabs, minScale);
  }
  const shown = toolSets.map((tools) => tools.hidden);
  let scale = 1;
  try {
    toolSets.forEach((measured) => {
      toolSets.forEach((tools) => {
        const hidden = tools !== measured;
        if (tools.hidden !== hidden) tools.hidden = hidden;
      });
      scale = Math.min(scale, solveLaidOutRow(bar, tabs, minScale));
    });
  } finally {
    toolSets.forEach((tools, index) => {
      if (tools.hidden !== shown[index]) tools.hidden = shown[index];
    });
  }
  return scale;
}

/**
 * Fit every tab label in one Foundry command bar. Returns the applied scale, or
 * null when there is nothing to fit (no bar, no tabs, or a detached/unlaid-out
 * surface, which is what jsdom and a pre-mount call both look like).
 *
 * @param {Element|null} bar the `.foundry-tabs` element
 * @returns {number|null}
 */
function fitFoundryTabs(bar) {
  if (!bar || typeof bar.querySelectorAll !== 'function') return null;
  const tabs = Array.from(bar.querySelectorAll('.foundry-tab'));
  if (!tabs.length) return null;

  // Always measure from the authored size. Reading a box through a scale left
  // over from a previous viewport is how a measured length outlives the box it
  // was taken from.
  applyScale(bar, 1);
  if (!(bar.getBoundingClientRect().width > 0)) {
    // No layout to reason about — a detached template, or jsdom. Leaving the
    // scale at 1 is the honest answer; guessing one would be a wall built out
    // of a missing number.
    return null;
  }

  const basePx = tabs.reduce((min, tab) => {
    const size = px(getComputedStyle(tab).fontSize);
    return size > 0 ? Math.min(min, size) : min;
  }, Infinity);
  const minScale = Number.isFinite(basePx) && basePx > 0
    ? Math.min(1, uiPx(MIN_FONT_PX) / basePx)
    : 1;

  const scale = solveEveryTabsRow(bar, tabs, minScale);
  applyScale(bar, scale);
  return scale;
}

/**
 * Keep one mounted surface fitted for as long as it is open.
 *
 * @param {Element|null} root the foundry surface root
 * @param {{signal?: AbortSignal}} [options]
 * @returns {{refit: () => number|null}}
 */
function watchFoundryTabFit(root, { signal } = {}) {
  const bar = root?.querySelector?.('.foundry-tabs') || null;
  const refit = () => fitFoundryTabs(bar);
  if (!bar) return { refit };

  let frame = 0;
  const coalesced = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      refit();
    });
  };

  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, signal ? { ...options, signal } : options);
  };

  // A language swap changes every advance width. `moonfall:i18n-translated`
  // fires on `document` in the same task the new text lands, so re-measuring
  // here means the player never sees a frame of the old size on the new words.
  listen(document, 'moonfall:i18n-translated', refit);
  // A resize is a gesture, not a load: one fit per frame is right, and the
  // debounced viewport event catches the orientation settle after it.
  listen(window, 'resize', coalesced, { passive: true });
  listen(window, 'orientationchange', coalesced, { passive: true });
  listen(window, 'moonfall:viewport-change', coalesced);

  if (signal) {
    signal.addEventListener('abort', () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }, { once: true });
  }

  refit();
  return { refit };
}

export {
  fitFoundryTabs,
  solveTabFitScale,
  watchFoundryTabFit,
  MIN_FONT_PX,
};
